import React, { useEffect, useState, useRef } from 'react'

export default function LightboxModal({ images = [], currentIndex = 0, isOpen = false, onClose, onIndexChange }) {
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const modalRef = useRef(null)

  const currentImage = images[currentIndex]

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.()
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < images.length - 1) {
          onIndexChange?.(currentIndex + 1)
        } else {
          onIndexChange?.(0)
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          onIndexChange?.(currentIndex - 1)
        } else {
          onIndexChange?.(images.length - 1)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, currentIndex, images.length, onClose, onIndexChange])

  if (!isOpen || !currentImage) return null

  const handlePrev = (e) => {
    e?.stopPropagation()
    const nextIdx = currentIndex > 0 ? currentIndex - 1 : images.length - 1
    onIndexChange?.(nextIdx)
  }

  const handleNext = (e) => {
    e?.stopPropagation()
    const nextIdx = currentIndex < images.length - 1 ? currentIndex + 1 : 0
    onIndexChange?.(nextIdx)
  }

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50

  const onTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      handleNext()
    } else if (isRightSwipe) {
      handlePrev()
    }
  }

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md p-4 select-none animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
    >
      {/* Top Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 md:p-6 bg-gradient-to-b from-black/80 to-transparent">
        <div className="text-xs uppercase tracking-widest text-neutral-400 font-mono">
          {currentIndex + 1} / {images.length}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-neutral-900/80 text-white border border-neutral-700/60 hover:bg-[#cbb05e] hover:border-[#cbb05e] hover:text-[#0f1013] transition-colors cursor-pointer"
            aria-label="Luk visning"
          >
            <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Prev Button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={handlePrev}
          className="absolute left-3 md:left-6 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-neutral-900/80 text-white border border-neutral-700/60 hover:bg-[#cbb05e] hover:border-[#cbb05e] hover:text-[#0f1013] transition-colors cursor-pointer focus:outline-none"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Image Container */}
      <div
        className="relative max-w-5xl max-h-[82vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={currentImage.src || currentImage}
          alt={currentImage.alt || currentImage.title || 'Docuit ink tatovering'}
          className="max-h-[80vh] max-w-full object-contain rounded-sm border border-neutral-800 shadow-2xl"
        />
      </div>

      {/* Next Button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={handleNext}
          className="absolute right-3 md:right-6 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-neutral-900/80 text-white border border-neutral-700/60 hover:bg-[#cbb05e] hover:border-[#cbb05e] hover:text-[#0f1013] transition-colors cursor-pointer focus:outline-none"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
